import * as React from 'react';

import * as styles from './Recommendation.css';

import { Label, UIHeading4, Body2, Caption } from '@cian/ui-kit';
import { useInView } from '@cian/valuation-utils-component';
import { TRecommendation } from '../../types/recommendation';
import { PriceDiscount } from '../PriceDiscount';
import { useImageLoadingHandlers } from '../Recommendation/hooks';

interface IRecommendationProps extends TRecommendation {
  onClick(analyticsData: object): void;
  onShowTrap(analyticsData: object): void;
  favoriteButton: React.ReactNode;
  isVisited: boolean;
  onTrack(offerId: number | null, position: number, contextToken?: string): void;
  position: number;
}

const CALLBACK_DELAY = 1000;

export const Recommendation: React.FC<IRecommendationProps> = ({
  imageUrl,
  title: price,
  description,
  analyticsData,
  offerId,
  href,
  transport,
  onClick,
  onShowTrap,
  favoriteButton,
  isVisited,
  onTrack,
  position,
  discount,
  contextToken,
}) => {
  const [trapShown, setTrapShown] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const { onLoad, onError } = useImageLoadingHandlers({
    domain: 'RecommendationsMicrofrontend',
    src: imageUrl,
  });

  React.useEffect(() => {
    if (!offerId && inView && !trapShown) {
      onShowTrap(analyticsData);
      setTrapShown(true);
    }

    // копируется логика ActionAfterViewObserver, так как он не умеет не оборачивать компонент в DIV
    if (inView) {
      timerRef.current = window.setTimeout(() => {
        onTrack(offerId, position, contextToken);

        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }, CALLBACK_DELAY);

      return () => {
        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    } else {
      return undefined;
    }
  }, [inView, offerId, analyticsData, onShowTrap, trapShown, onTrack, position, contextToken]);

  return (
    <a
      ref={ref}
      onClick={() => onClick(analyticsData)}
      href={href}
      target="_blank"
      className={styles['container']}
      rel="noreferrer"
    >
      {isVisited && (
        <Label key="visited" background="overlay-default" color="theme-white" className={styles['visited-label']}>
          Вы смотрели
        </Label>
      )}
      <span className={styles['favorite']}>{favoriteButton}</span>
      <img src={imageUrl} className={styles['image']} onLoad={onLoad} onError={onError} />
      <div className={styles['body']}>
        <div className={styles['price']}>
          <span className={styles['price-value']}>
            <UIHeading4>{discount ? discount.newPrice : price}</UIHeading4>
          </span>
          {discount && <PriceDiscount oldPrice={discount.oldPrice} percent={discount.percent} />}
        </div>
        <Body2 ellipsis>{description}</Body2>
      </div>
      {transport && (
        <Caption color="text-secondary-default" ellipsis>
          {transport}
        </Caption>
      )}
    </a>
  );
};
